import { RootState } from "src/store";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createGuid, hasValue, Nullable } from "@itk/typescript-utils";

import { handleOnFieldChange, OptionalFormController, OptionalFormInclude, required, useForm } from "@itk/react-form";
import { Checkbox, FormControlLabel, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { Button, CancelButton, FormText, SaveAndCancelButton, TooltipShortcut } from "@itk/mui-components";
import { connect } from "react-redux";
import { userManagementActions } from "../../user-management-store";
import { AddEditDialogProp, ContactPersonExtendedGet, ContactPersonPermissionReplacePut, ContactPersonPut } from "../../user-management-types";
import { Shared_ContactPersonPermissionKinds } from "src/backend/openapi/v1";
import { getContactPersonPermissions, usePermissionsTranslation } from "src/components";
import { informationPageUsersSectionK, useInformationPageUsersSectionT } from "src/translation-hooks/information-page-users-section";
import { useInformationPageUsersSectionAddEditUserDialogT } from "src/translation-hooks/information-page-users-section-add-edit-user-dialog";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { useInformationPageT } from "src/translation-hooks/information-page";
import { useGetCustomerId } from "src/hooks";
import { GenericDialog } from "src/components/generic-dialog";

const mapStateToProps = (state: RootState) => ({
  ...state.pageReducer.informationPageReducer.userMananementReducer,
  currentCustomer: state.generalReducer.applicationReducer.currentCustomer
});

const ownActions = { ...userManagementActions };

type Props = ReturnType<typeof mapStateToProps> & typeof ownActions & { addEditDialogInfo: AddEditDialogProp };

const AddEditUserDialogBase = (props: Props) => {
  const {
    addEditDialogInfo,
    editContactPersonLoading,
    setAddEditDialogProps,
    setHasChanges,
    submitting,
    emailExistsRequest,
    getContactPersonByContactPersonIdRequest,
    putContactPersonPermissionRequest,
    currentCustomer
  } = props;

  const theme = useTheme();

  const informationPageT = useInformationPageT();
  const informationPageUsersSectionT = useInformationPageUsersSectionT();
  const informationPageUsersSectionAddEditUserDialogT = useInformationPageUsersSectionAddEditUserDialogT();
  const genericTranslationsT = useGenericTranslationsT();

  const { selectedContactPersonId, variant } = addEditDialogInfo;
  const { getPermissionTranslation } = usePermissionsTranslation();

  const { customerId } = useGetCustomerId();

  useEffect(() => {
    if (variant === "edit") {
      getContactPersonByContactPersonIdRequest(selectedContactPersonId);
    }
  }, [selectedContactPersonId, variant, getContactPersonByContactPersonIdRequest]);

  const initFormController = useMemo(
    (): OptionalFormController<ContactPersonPut> => ({
      email: { validators: [required] },
      contactPersonId: { validators: [required] },
      customerId: { validators: variant === "edit" ? [required] : [] },
      permissions: { validators: variant === "edit" ? [required] : [] }
    }),
    [variant]
  );
  const include = useMemo((): OptionalFormInclude<ContactPersonPut> => ({ email: true, contactPersonId: true, customerId: true, permissions: true }), []);

  const contactPersonData = useMemo(
    (): Nullable<ContactPersonExtendedGet> =>
      variant === "edit" && editContactPersonLoading.loading === "has-loaded"
        ? {
            ...editContactPersonLoading.data
          }
        : {
            contactPersonId: createGuid(),
            customerId: currentCustomer.loading === "has-loaded" ? currentCustomer.data.customerId : "",
            email: "",
            fullName: "",
            identityId: "",
            inActive: false,
            isAdmin: false,
            permissions: []
          },
    [editContactPersonLoading, variant, selectedContactPersonId]
  );

  const { validate, getValues, hasChanges, formController, formState } = useForm<ContactPersonPut & { permissions: Shared_ContactPersonPermissionKinds[] }>(
    contactPersonData,
    initFormController,
    include
  );

  const [disableSaveBtn, setDisableSaveBtn] = useState<boolean>(true);

  const onSave = useCallback(() => {
    const values = getValues();
    const errors = validate();
    if (!customerId) return;

    if (!hasValue(errors) && hasValue(values)) {
      if (variant === "create" && hasValue(values.email)) {
        emailExistsRequest(values.email, createGuid(), customerId);
        setHasChanges(true);
        setAddEditDialogProps(undefined);
      } else {
        if (values.contactPersonId) {
          const data: ContactPersonPermissionReplacePut = {
            contactPersonId: values.contactPersonId,
            permissions: values.permissions || []
          };

          putContactPersonPermissionRequest(values.contactPersonId, data);
        }
        setHasChanges(true);
      }
    }
  }, [getValues, emailExistsRequest, setHasChanges, setAddEditDialogProps, validate, putContactPersonPermissionRequest, variant, customerId]);

  useEffect(() => {
    if (submitting) setDisableSaveBtn(true);
    else if (hasChanges) setDisableSaveBtn(false);
    else setDisableSaveBtn(true);
  }, [submitting, hasChanges]);

  return (
    <GenericDialog
      headerTitle={variant === "edit" ? informationPageUsersSectionT.editUser : informationPageUsersSectionT.newUser}
      onClose={() => setAddEditDialogProps(undefined)}
      renderDialogActions={
        variant === "create" ? (
          <>
            <TooltipShortcut shortcut={{ action: onSave, shortCutKey: "g", modifier: "alt" }}>
              <Button t={informationPageUsersSectionT} k={informationPageUsersSectionK} field="sendInvitation" onClick={onSave} variant="contained" disabled={disableSaveBtn} />
            </TooltipShortcut>
            <CancelButton onCancel={() => setAddEditDialogProps(undefined)} disabled={submitting} />
          </>
        ) : (
          <SaveAndCancelButton
            cancelButtonProps={{ size: "small" }}
            saveButtonProps={{ size: "small" }}
            onCancel={() => setAddEditDialogProps(undefined)}
            onSave={onSave}
            disableSave={disableSaveBtn}
            disableCancel={submitting}
          />
        )
      }
    >
      {editContactPersonLoading.loading === "has-loaded" || variant === "create" ? (
        <Stack gap={2} width="100%">
          {variant === "edit" ? (
            <Stack gap={4}>
              <Stack gap={0.5}>
                <Typography variant="subtitle2">{informationPageT.information}</Typography>
                <Stack gap={1} direction="row">
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: theme.palette.customColors.darkGrey
                    }}
                  >
                    {genericTranslationsT.name}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: theme.palette.customColors.darkGrey
                    }}
                  >
                    {contactPersonData.fullName}
                  </Typography>
                </Stack>
                <Stack gap={1} direction="row">
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: theme.palette.customColors.darkGrey
                    }}
                  >
                    {genericTranslationsT.email}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: theme.palette.customColors.darkGrey
                    }}
                  >
                    {contactPersonData.email}
                  </Typography>
                </Stack>
              </Stack>
              <Stack>
                <Typography variant="subtitle1" fontWeight={theme => theme.typography.fontWeightBold}>
                  {informationPageUsersSectionAddEditUserDialogT.profileAccess}
                </Typography>
                {Object.values(getContactPersonPermissions())
                  .sort((x, y) => getPermissionTranslation(x).localeCompare(getPermissionTranslation(y)))
                  .map(option => (
                    <FormControlLabel
                      key={option}
                      label={getPermissionTranslation(option)}
                      sx={{
                        "& .MuiTypography-root": {
                          fontSize: theme.typography.subtitle1.fontSize,
                          color: theme.palette.customColors.darkGrey
                        }
                      }}
                      control={
                        <Checkbox
                          checked={formState.permissions.value?.includes(option) ?? false}
                          onChange={e => {
                            if (formState.permissions.value) {
                              const permissions = formState.permissions.value;
                              if (e.target.checked) {
                                permissions.push(option);
                              } else {
                                const index = permissions.indexOf(option);
                                permissions.splice(index, 1);
                              }
                              handleOnFieldChange(permissions, formController.permissions);
                            }
                          }}
                        />
                      }
                    />
                  ))}
              </Stack>
            </Stack>
          ) : (
            <Stack gap={2}>
              <Typography variant="subtitle1" fontWeight={theme => theme.typography.fontWeightBold}>
                {informationPageUsersSectionAddEditUserDialogT.inviteUserViaEmail}
              </Typography>
              <Typography variant="subtitle1">{informationPageUsersSectionAddEditUserDialogT.inviteUserMesssage}</Typography>
              <FormText
                autoFocus
                width="flex"
                t={genericTranslationsT}
                field="email"
                size="small"
                k={genericTranslationsK}
                formFieldState={formState.email}
                formFieldController={formController.email}
                onKeyUp={e => {
                  if (e.key === "Enter") onSave();
                }}
              />
            </Stack>
          )}
        </Stack>
      ) : (
        <Stack gap={3} flex={1}>
          <Stack gap={2}>
            <Typography variant="subtitle1">
              <Skeleton width="40%" />
            </Typography>
            <Stack gap={2} flex={1}>
              <Skeleton variant="text" sx={{ flex: 1 }} />
              <Skeleton variant="text" sx={{ flex: 1 }} />
            </Stack>
          </Stack>
          <Stack gap={2}>
            <Typography variant="subtitle1" flex={0.4}>
              <Skeleton width="40%" />
            </Typography>
            <Stack alignItems="center" direction="row">
              <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
              <Skeleton sx={{ flex: 0.6 }} />
            </Stack>
            <Stack alignItems="center" direction="row">
              <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
              <Skeleton sx={{ flex: 0.7 }} />
            </Stack>
            <Stack alignItems="center" direction="row">
              <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
              <Skeleton sx={{ flex: 0.5 }} />
            </Stack>
          </Stack>
        </Stack>
      )}
    </GenericDialog>
  );
};

export const AddEditUserDialog = connect(mapStateToProps, ownActions)(AddEditUserDialogBase);
